import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Flame } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);
  const cart = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!clearedRef.current) {
            cart.clear();
            clearedRef.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
          setState("failed");
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 py-20">
      <div
        data-testid="success-page"
        className="w-full max-w-xl bg-[#0f0f0f] border border-white/10 p-10 md:p-14 text-center"
      >
        <div className="flex justify-center mb-6">
          <Flame className="w-8 h-8 text-[#FF3B30]" />
        </div>

        {state === "checking" && (
          <div data-testid="success-checking">
            <Loader2 className="w-10 h-10 text-[#FF8C00] mx-auto animate-spin" />
            <h1 className="font-['Anton'] text-4xl uppercase tracking-tight text-white mt-6">
              Confirming your order…
            </h1>
            <p className="font-['Outfit'] text-zinc-400 mt-3">
              Hang tight — checking with Stripe.
            </p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="success-paid">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h1 className="font-['Anton'] text-5xl uppercase tracking-tight text-white mt-6 leading-none">
              Order Locked In
            </h1>
            <p className="font-['Outfit'] text-zinc-400 mt-3">
              Payment received. We're firing up the smoker.
            </p>
            {details && (
              <div className="mt-8 text-left bg-black/40 border border-white/10 p-5">
                <p className="font-['Outfit'] text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                  Receipt
                </p>
                <ul className="space-y-2 font-['Outfit'] text-sm text-zinc-300">
                  {(details.cart_summary || []).map((line) => (
                    <li key={line.item_id} className="flex justify-between gap-4">
                      <span>
                        {line.quantity} × {line.name}
                      </span>
                      <span className="text-white">
                        ${(line.line_total_cents / 100).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-white/10 mt-4 pt-4 flex justify-between items-center">
                  <span className="font-['Outfit'] text-xs uppercase tracking-[0.3em] text-zinc-500">
                    Total
                  </span>
                  <span className="font-['Anton'] text-2xl text-white">
                    ${(details.amount_total_cents / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {state === "expired" && (
          <div data-testid="success-expired">
            <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto" />
            <h1 className="font-['Anton'] text-4xl uppercase tracking-tight text-white mt-6">
              Session Expired
            </h1>
            <p className="font-['Outfit'] text-zinc-400 mt-3">
              The checkout session timed out. Try again from the menu.
            </p>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="success-failed">
            <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto" />
            <h1 className="font-['Anton'] text-4xl uppercase tracking-tight text-white mt-6">
              Couldn't Confirm
            </h1>
            <p className="font-['Outfit'] text-zinc-400 mt-3">
              We couldn't verify the payment right now. If you were charged,
              you'll get a Stripe email receipt.
            </p>
          </div>
        )}

        <Link
          to="/"
          data-testid="success-home-link"
          className="inline-flex mt-10 items-center font-['Anton'] tracking-widest uppercase border border-white/30 hover:border-white text-white px-6 py-3 text-sm transition-colors"
        >
          Back to Menu
        </Link>
      </div>
    </div>
  );
}
